import { useMemo } from "react";
import { Link } from "react-router";
import { ChartBox, ChartInfo } from "@/components/ChartInfo";
import { type HeadCard, Headline } from "@/components/Headline";
import { ActionCategory } from "@/generated/core/v1/actions";
import { money } from "@/plan-logic/lib/format";
import { spendingByExpense } from "@/plan-logic/plan-view";
import { abiKeptCard } from "../cashflow/cashHead";
import { CATEGORY } from "../categories";
import { ConnectEmpty } from "../ConnectEmpty";
import { PlanPage, type PlanCtx } from "../PlanPage";
import "@/styles/v47-pages.css";

/** What the budget counts: the household's spending, not debt, savings, or priorities. */
const SPENDING = new Set([
  ActionCategory.ESSENTIAL,
  ActionCategory.HOUSING,
  ActionCategory.LIFESTYLE,
  ActionCategory.OTHER,
  ActionCategory.UNSPECIFIED,
]);

/**
 * Prototype v41/v42's Budget page. Its default budget is the twelve-month average of categorised
 * bank transactions, with a set-your-own figure per category, alerts at 90% and 100%, and each
 * month against the budget. None of that data exists yet (no transactions, no stored budget), so
 * the default budget here is the household's own spending as the plan carries it (each expense, a
 * month, over the plan's next twelve months), the set-your-own column and the alerts say they
 * aren't saved yet, and the month-by-month view waits on bank connections.
 */
function Body({ ctx }: { ctx: PlanCtx }) {
  const { abi, naive, index } = ctx;
  // What you spend on the path you're on (the same expenses on either run).
  const base = naive ?? abi;
  // Expenses the household never named (ones the engine adds, such as a property tax default) would
  // all read as their category: one row per category for those, so no two rows share a label.
  const rows = useMemo(() => {
    const out = new Map<string, { uuid: string; category: ActionCategory; monthly: number }>();
    spendingByExpense(base, 12)
      .filter((e) => SPENDING.has(e.category))
      .forEach((e) => {
        const named = index.name(e.uuid, "") !== "";
        const key = named ? e.uuid : `category:${e.category}`;
        const row = out.get(key);
        if (row) row.monthly += e.monthly;
        else out.set(key, { ...e, uuid: named ? e.uuid : key });
      });
    return [...out.values()];
  }, [base, index]);
  const total = rows.reduce((t, r) => t + r.monthly, 0);

  const head: HeadCard[] = [
    {
      k: "This month",
      v: "Waiting on your accounts",
      pending: true,
      s: "How this month is going against your budget comes from your bank transactions.",
    },
    {
      k: "Next 12 months",
      v: `${money(total)} spending/month`,
      s: "what your plan spends on your expenses, averaged over the year",
      testId: "bud-avg",
    },
  ];
  if (naive) head.push(abiKeptCard(naive, abi, "spare/month"));

  return (
    <>
      <Headline id="hd-bud" cards={head} />

      <div className="card pad" style={{ marginBottom: 16 }}>
        <h2 style={{ marginBottom: 6 }}>Your default budget is what you already spend</h2>
        <p className="mut" style={{ marginBottom: 14, maxWidth: "74ch" }}>
          Each expense below starts at what your plan spends on it, averaged over the next twelve
          months. Nothing to set up: if you do nothing, that is your budget. Turn the alerts on and
          Abi will tell you when a category reaches 90% and again at 100%, so you hear about it
          while there is still a month left to do something.
        </p>
        <div className="budalerts" id="bud-alerts" data-testid="bud-alerts">
          {[
            ["Tell me at 90%", "while there is still a month left to change something"],
            ["Tell me at 100%", "the moment a category is spent out"],
          ].map(([b, em]) => (
            <label className="budalert" key={b}>
              <input type="checkbox" disabled />
              <span>
                <b>{b}</b>
                <em>{em}</em>
              </span>
            </label>
          ))}
        </div>
        <p className="hint" style={{ margin: "10px 0 0" }}>
          Alerts aren&rsquo;t available yet: they need your bank transactions to know what
          you&rsquo;ve spent, and a place to keep your settings. Both are coming.
        </p>
      </div>

      <div className="card pad" style={{ marginBottom: 16 }}>
        <div style={{ display: "flex", alignItems: "baseline", marginBottom: 4 }}>
          <h2>Actual spending against budget, month by month</h2>
        </div>
        <p className="mut" style={{ marginBottom: 12 }}>
          Above the line is over, below is under. Click a month to see which categories took it
          there.
        </p>
        <ChartBox id="bud-months">
          <ConnectEmpty what="Each month's spending against your budget comes from your transactions." />
        </ChartBox>
      </div>

      <div className="card pad" style={{ marginBottom: 16 }}>
        <div className="cf-bar" style={{ marginBottom: 6 }}>
          <h2>
            Your average spending over the next 12 months{" "}
            <span className="mut" style={{ fontWeight: 400 }}>
              (default budget)
            </span>
            <ChartInfo k="cats" inline />
          </h2>
        </div>
        <p className="mut" style={{ marginBottom: 14, maxWidth: "80ch" }}>
          Leave the last column alone to keep the average. Put a figure in and that becomes the line
          you are measured against, here and in the alerts.
        </p>
        <div id="cats" data-testid="bud-table">
          {rows.length === 0 ? (
            <p className="mut">No spending in your plan yet. Add it on Update Data.</p>
          ) : (
            <>
              <div className="budhead">
                <span>Expense</span>
                <span>A month, on average</span>
                <span>Set your own budget</span>
              </div>
              {rows.map((r) => {
                const name = index.name(r.uuid, CATEGORY[r.category].label);
                return (
                  <div className="budrow" key={r.uuid}>
                    <span className="bn">
                      {name}
                      {name !== CATEGORY[r.category].label && (
                        <span className="sub">{CATEGORY[r.category].label}</span>
                      )}
                    </span>
                    <span className="ba tnum">{money(r.monthly)}</span>
                    <span className="bi">
                      <span className="ofield">
                        <span className="pfx">$</span>
                        <input
                          type="number"
                          min="0"
                          step="10"
                          disabled
                          placeholder={String(Math.round(r.monthly))}
                          aria-label={`Your budget for ${name}`}
                        />
                      </span>
                    </span>
                  </div>
                );
              })}
              <div className="budrow tot">
                <span className="bn">Total</span>
                <span className="ba tnum">{money(total)}</span>
                <span />
              </div>
            </>
          )}
        </div>
        <div className="budsave">
          <button type="button" className="btn p" disabled>
            Save my budget
          </button>
          <span className="mut" data-testid="bud-not-saved">
            Setting your own budget isn&rsquo;t saved yet. To change what your plan spends, use{" "}
            <Link className="wp-link" to="/app/update-data?open=spending">
              Update Data
            </Link>
            .
          </span>
        </div>
      </div>
    </>
  );
}

/** Prototype v41/v42's Budget page. */
export function BudgetPage() {
  return (
    <PlanPage title="pages.budget.title" sub="pages.budget.sub" testId="budget">
      {(ctx) => <Body ctx={ctx} />}
    </PlanPage>
  );
}
